import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from '../../error-handling/error-reporter-domain';

const API_BASE_PATH = process.env.API_BASE_PATH || '';

// eslint-disable-next-line default-param-last
function getFetchHeaders(payload = {}, method, urlencoded, additionalHeaders = {}, additionalOptions = {}) {
	const appJson = 'application/json; charset=utf-8';
	const appUrle = 'application/x-www-form-urlencoded; charset=UTF-8';
	const contentType = urlencoded ? appUrle : appJson;
	let options = {
		headers: {
			'Content-Type': contentType
		},
		cache: 'no-store',
		credentials: 'include'
	};
	if (payload instanceof FormData) {
		delete options.headers['Content-Type'];
	}
	if (additionalHeaders) {
		options = {
			...options,
			headers: {
				...options.headers,
				...additionalHeaders
			}
		};
	}
	if (additionalOptions) {
		options = {
			...options,
			...additionalOptions
		};
	}
	if (method === 'GET' || method === 'HEAD') {
		return options;
	}

	// TODO: if the content-type is urlencoded, the payload is a urlencoded string
	// like key1=value1&key2=value2&key3=value3
	// This may be handled here or elsewhere, or show warning.

	const body = typeof payload === 'string' || payload instanceof FormData ? payload : JSON.stringify(payload);
	// in case of the payload is a string, the JSON.stringify will wrap
	// the string inside extra quotations.
	// JSON.stringify("foo") === ""foo"
	// body: JSON.stringify(payload)

	return {
		...options,
		...{
			method,
			body
		}
	};
}

export const doAPIRequest = ({
	endpoint = '/',
	payload = {},
	method = 'GET',
	urlencodedContentType = false,
	additionalHeaders = {},
	additionalOptions = {}
}) => {
	const headers = getFetchHeaders(payload, method, urlencodedContentType, additionalHeaders, additionalOptions);
	return fetch(`${API_BASE_PATH}${endpoint}`, headers);
};

/**
 * @deprecated Use `requestApi` from '@comeon/web-core/utils/api/api-new' instead.
 * The new API uses a single object parameter with named properties for better maintainability.
 */
async function requestApi(
	endpoint = '/',
	payload = {},
	method = 'GET',
	urlencodedContentType = false,
	additionalHeaders = {},
	additionalOptions = {}
) {
	let response;
	try {
		response = await doAPIRequest({
			endpoint,
			payload,
			method,
			urlencodedContentType,
			additionalHeaders,
			additionalOptions
		});

		const contentType = response.headers.get('content-type') || '';

		if (!response.ok) {
			let errorBody;
			if (contentType.includes('application/json')) {
				errorBody = await response.json().catch(() => ({}));
			} else {
				const responseText = await response.text();
				errorBody = {
					status: 'FAILURE',
					errorCode: 'HTTP_ERROR',
					errorDescription: `HTTP ${response.status}, result: ${responseText}`
				};
			}
			reportException(new Error(JSON.stringify(errorBody)), {
				domain: ErrorReporterDomain.API,
				extra: {
					type: 'response-handling',
					url: window.location,
					endpoint,
					status: response.status,
					fullResponse: response
				}
			});
			return errorBody;
		}

		return await response.json();
	} catch (err) {
		throw new Error(`${endpoint}, ${response?.status || 'no response'}`, {
			cause: err
		});
	}
}

const parseResponse = ({
	status = '',
	errorCode = '',
	errorDescription = '',
	result = { error: { errorCode, errorDescription, status } }
}) => result;

export { requestApi, parseResponse };
